import { useSiteContent } from '@/hooks/useSiteContent';
import { useTranslation } from 'react-i18next';
import ContentState from '@/components/feature/ContentState';
import { Link } from 'react-router-dom';

interface SpecialServicesHeroData {
  eyebrow: string;
  title: string;
  highlight: string;
  description: string;
  image: string;
}

export default function SpecialServicesHero() {
  const { data, loading, error, refresh } = useSiteContent<SpecialServicesHeroData>(
    'special_services_hero',
  );
  const { t } = useTranslation();

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="relative w-full min-h-[420px] md:min-h-[520px] flex items-center justify-center overflow-hidden pt-20">
      <img
        src={data.image}
        alt={t('ss.heroAlt')}
        title={t('ss.heroTitle')}
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
        <p className="text-accent-400 text-sm md:text-base tracking-wide uppercase mb-3">
          {data.eyebrow}
        </p>
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
          {data.title} <span className="text-accent-400">{data.highlight}</span>
        </h1>
        <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
          {data.description}
        </p>

        <nav className="mt-8 flex items-center justify-center gap-2 text-sm text-white/70">
          <Link to="/" className="hover:text-white transition-colors">{t('ss.home')}</Link>
          <span>/</span>
          <span className="text-white">{t('ss.specialServices')}</span>
        </nav>
      </div>
    </section>
  );
}